import { motion } from 'framer-motion';
import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import { CloseButton } from '../../../CodesThatsGottaGoSomewhereElse';
import { Icon } from '../../../concepts/resources/Icon';
import { Colors } from '../../../ui';
import { Position } from '../../../ui/Position';
import { Shadows } from '../../../ui/Standards';
import { useResolve } from '../../Container';
import { Copy, Flexer, Title } from '../../dashboard/elements/LayoutElements';
import { HelpController } from './HelpController';
import { HelpVideo } from './HelpVideo';

const motionConfig = {
    initial: 'before',
    animate: 'during',
    exit: 'after',
    variants: {
        before: {
            opacity: 0,
            scale: 1,
            y: '-5rem',
        },
        during: {
            opacity: 1,
            scale: 1,
            x: 0,
            y: 0,
            transition: {
                ease: [0, .76, .47, 1],
                duration: .6,
            }
        },
        after: {
            opacity: 0,
            scale: .9,
            x: 0,
            y: 0,
            transition: {
                ease: [0, .76, .47, 1],
                duration: .15,
            }
        }
    }
};

const HelpVideoOverlay = styled.div<{ cursor?: string, size: 'sm' | 'lg' }>`
   display: flex;
   align-items: center;
   justify-content: center;
   background: rgba(0, 0, 0, .3);
   border-radius: .25rem;
   color: white;
   cursor: ${p => p.cursor || 'pointer'};

   ${p => p.size === 'lg' && css`
       padding: 1rem;
       font-weight: 500;
       font-size: 2rem;

       svg {
           width: 3rem;
           height: 3rem;
       }
   `}

   ${p => p.size === 'sm' && css`
       padding: .5rem;
       font-weight: 450;
       font-size: 1rem;
       gap: 0.25rem;
   `}

   &:hover {
       background: rgba(0, 0, 0, .4);
   }
   &:active {
       background: rgba(0, 0, 0, .5);
   }
`;

const HelpVideoMinMax = ({ size, onClick }: { size: 'min' | 'max', onClick: () => void }) => {
    return <HelpVideoOverlay size="sm" onClick={onClick}>
        <Icon src={`deqo://icons/hero/arrow-${size === 'min' ? 'up' : 'down'}@[1,1,-45]`} />
        {size === 'max' ? 'Minimize' : 'Maximize'}
    </HelpVideoOverlay>;
};

const HelpVideoClose = () => {
    const ctrl = useResolve(HelpController);
    return <HelpVideoOverlay size="sm" onClick={() => ctrl.dismissVideo()}>
        <CloseButton size="1.25" />
        Close
    </HelpVideoOverlay>;
};

const HelpVideoFrame = styled.div<{ size?: string }>`

   position: absolute;
   top: 0%;
   left: 50%; 
   width: 64rem;
   height: 36rem;
   transform: translate(-50%, 0) translate(0, 2rem);
   box-shadow: ${Shadows.xxl};
   border-radius: .5rem;
   overflow: hidden;

   ${Title}, ${Copy}, a {
       color: ${Colors.text};
   }
   
   background: ${Colors.mystery};
   color: white;

   video {
       max-height: 100%;
   }

   ${HelpVideoOverlay} {
       opacity: 0;
       transition: opacity .25s linear;
   }

   &:hover {
       ${HelpVideoOverlay} {
           opacity: 1;
           transition: none;
       }
   }

   ${p => p.size === 'min' && css`
       top: 100%;
       left: 100%; 
       width: 48rem;
       height: 27rem;
       transform: translate(-100%, -100%) translate(-1rem, -1rem);
   `}

   transition: all .25s ease-in-out;
`;

export const HelpVideoView = ({ videoUrl }: HelpVideo) => {

    const styling = {
        left: '50%',
        top: '1rem',
        transform: 'translate(-50%, 0)',
        pointerEvents: 'all' as const,
        userSelect: 'none' as const,
    };

    const [size, setSize] = useState('max' as 'min' | 'max');
    const onToggleSize = () => setSize(size === 'min' ? 'max' : 'min');

    const onPlay = (e: any) => {
        const t = e.target;
        t.style.opacity = 1;
        if (t.currentTime >= t.duration - 1) {
            t.currentTime = 0;
        }
    };

    const onEnded = (e: any) => {
        const t = e.target;
        t.pause();
        t.currentTime = t.duration - 1;
    };

    return <motion.div style={styling} {...motionConfig}>
        <HelpVideoFrame size={size}>
            <video style={{ opacity: 0 }} autoPlay controls onPlay={onPlay} onEnded={onEnded} >
                <source src={videoUrl} type="video/mp4" />
            </video>
            <Position at="topRight" inset="1">
                <Flexer flex="row" center gap=".5">
                    <HelpVideoMinMax size={size} onClick={onToggleSize} />
                    <HelpVideoClose />
                </Flexer>
            </Position>
        </HelpVideoFrame>
    </motion.div>;
};
